<template>
	<div v-if="loading" class="flex w-full justify-center px-8 sm:px-20 py-32 md:py-12">
		<div class="text-center text-ihh-slate-gray py-16">Loading…</div>
	</div>
	<div v-else-if="page" class="flex w-full justify-center px-8 sm:px-20 py-32 md:py-12">
		<div class="w-full max-w-3xl lg:max-w-4xl">
			<div class="static-page" v-safe-html="page.html"></div>
		</div>
	</div>
	<NotFound v-else />
</template>

<script>
import axios from 'axios'
import NotFound from './404.vue'
import { normalizeStaticRoute } from '../../../../config/helpers/staticPages/routes.js'

export default {
	name: 'CmsPage',
	components: { NotFound },
	inject: ['store'],
	data() {
		return {
			page: null,
			loading: true,
			styleEl: null,
			requestId: 0,
			metaDescriptionState: null,
		}
	},
	watch: {
		'$route.path'() {
			this.restoreMetaDescription()
			this.load()
		},
		'page.css'() {
			this.syncCss()
		},
	},
	mounted() {
		this.load()
	},
	unmounted() {
		this.requestId += 1
		this.styleEl?.remove()
		this.restoreMetaDescription()
	},
	methods: {
		applyMetaDescription(description) {
			const text = typeof description === 'string' ? description.trim() : ''
			if (!text) {
				this.restoreMetaDescription()
				return
			}
			if (!this.metaDescriptionState) {
				const existing = document.head.querySelector('meta[name="description"]')
				this.metaDescriptionState = existing
					? { tag: existing, previous: existing.getAttribute('content'), created: false }
					: { tag: null, previous: null, created: false }
			}
			const state = this.metaDescriptionState
			if (!state.tag || !state.tag.isConnected) {
				const tag = document.createElement('meta')
				tag.setAttribute('name', 'description')
				document.head.appendChild(tag)
				state.tag = tag
				state.created = true
			}
			state.tag.setAttribute('content', text)
		},
		restoreMetaDescription() {
			const state = this.metaDescriptionState
			if (!state) return
			this.metaDescriptionState = null
			if (state.created) {
				state.tag?.remove()
				return
			}
			if (state.tag?.isConnected) {
				if (state.previous == null) state.tag.removeAttribute('content')
				else state.tag.setAttribute('content', state.previous)
			}
		},
		async load() {
			const requestId = ++this.requestId
			this.loading = true
			this.page = null
			this.styleEl?.remove()
			this.styleEl = null

			const normalized = normalizeStaticRoute(this.$route.path)
			if (!normalized.ok) {
				if (requestId === this.requestId) this.loading = false
				return
			}

			try {
				const encoded = normalized.slug.split('/').map(encodeURIComponent).join('/')
				const { data } = await axios.get(`${this.store.api}/controllers/staticPages/published/${encoded}`)
				if (requestId !== this.requestId) return
				if (data?.success && data.page?.html) {
					this.page = data.page
					document.title = data.page.metaTitle || data.page.title || document.title
					this.applyMetaDescription(data.page.metaDescription)
					this.syncCss()
				}
			} catch {
				// A missing page, or a CMS outage, keeps the existing 404 page.
			} finally {
				if (requestId === this.requestId) this.loading = false
			}
		},
		syncCss() {
			if (!this.page?.css) {
				this.styleEl?.remove()
				this.styleEl = null
				return
			}
			if (!this.styleEl) {
				this.styleEl = document.createElement('style')
				this.styleEl.setAttribute('data-static-page', 'cms')
				document.head.appendChild(this.styleEl)
			}
			this.styleEl.textContent = this.page.css
		},
	},
}
</script>
